'use client'
import "@/components/ReviewContainer/ReviewContainer.css";
import ReviewDetails from "@/components/ReviewDetails/ReviewDetails";
import PopupModal from "@/components/PopupModal/PopupModal";
import {React, useState, useEffect} from 'react';
import chunk from 'lodash/chunk';
import orderBy from 'lodash/orderBy';
import { fetchGoogleReviews } from "@/API/Review/reviewAPI";

/**
 * @description The ReviewContainer component is a container for the review section of the application. It is responsible for rendering the review layout and managing the state of the reviews.
 * @returns {JSX.Element} The rendered ReviewContainer component.
 */
export default function ReviewContainer({filters}) {
  // State to hold the reviews.
  const [reviews, setReviews] = useState([]);

  // State to hold the review details.
  const [reviewDetails, setReviewDetails] = useState(null);

  // Separated reviews into pairs for layout purposes.
  const [pairs, setPairs] = useState([]);

  // State to hold the current page config for pagination.
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [biLayer, setBiLayer] = useState(true);
  const [noMorePages, setNoMorePages] = useState(false);
  const [totalReviews, setTotalReviews] = useState(null);

  // States for popup modal
  const [popUpOpen, setPopupOpen] = useState(false);
  const [popUpTitle, setPopupTitle] = useState("");
  const [popUpMessage, setPopupMessage] = useState("");

  /**
   * @description Computes the layout of the reviews based on the screen size. This can be used to determine how many reviews to show per page and how to arrange them.
   * @param {number} width width of the user's screen.
   * @param {number} height height of the user's screen.
   */
  function computeLayout(width, height) {
    // Set page size here
  }

  // Fetch reviews from backend and apply filters/sorts before setting state.
  /**
   * 
   * @param {number} pageNumber The page number of reviews to retrieve, defaults to 1, at the "beginning".
   * @param {number} pageSize The number of reviews per page, defaults to 20, which is a reasonable number for a computer screen.
   * @param {boolean} biLayer Whether to arrange reviews in a bi-layer format (2 reviews per row) or a single column format (1 review per row).
   *  Defaults to true for bi-layer, which is more visually appealing on larger screens.
   *  Single column may be better for mobile devices.
   *  This can also be determined dynamically based on screen size using the computeLayout function.
   * @param {Object} filters An object containing filter and sort criteria, such as rating, search term, order by, and order direction.
   *  The filters will be sent to backend to retrieve a subset of reviews that match the criteria.
   * 
   * TODO: implement different sites for reviews in the future
   */
  async function fetchReviews(pageNumber = 1, pageSize = 20, biLayer = true, filters) {
    // TODO: make filters separate
    const response = await fetchGoogleReviews(pageNumber, pageSize, filters);

    // Check to see if response failed and not logged in, if so then it didn't really fail
    if (!response.success) {
      if (response.pageToBeFetched != null && !response.pageToBeFetched) {
        setPageNumber(Math.max(1, pageNumber - 1)); // Go back to previous page if no more pages to show or back to first page
        setNoMorePages(true);
        return; // No more pages to fetch, exit without showing an error
      }

      // TODO: should probably just have a home page instead
      if (response.message.includes("Unauthorized") || response.message.includes("Demo phase")) {
        setPopupTitle("Demo phase");
        setPopupMessage("Login and link your google account with business permission to view your reviews.\n" + 
          "Currently the Google account connection is reset every 7 days due to Google API restrictions. Please re-link your account if you are unable to view your reviews.");
        setPopupOpen(true);
        return;
      }

      setPopupTitle("Error");
      setPopupMessage(response.message || "Failed to fetch reviews. Please try again.");
      setPopupOpen(true);
      return;
    }

    let fetchedReviews = response.reviews || [];
    setTotalReviews(response.totalReviews || 0);
    console.log("\nRaw fetched reviews:", fetchedReviews); //debug log
    const ratingMap = {ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5};
    const normalizedReviews = fetchedReviews.map(r => {
      // Converting ratings in string (returned by google) to numbers
      const rawRating = r.starRating;
      let rating = 0;
      const asNum = parseInt(rawRating, 10);
      rating = Number.isFinite(asNum) ? asNum : ratingMap[rawRating.toUpperCase()] || 0;

      // In format year month day
      const rawDate = r.updateTime || r.createTime || "";
      const date = rawDate ? new Date(rawDate).toISOString().split('T')[0] : "Unknown date";
      
      // Strip leading and trailing " in the comments
      // We strip whitespaces, ", "", ' and ''
      let content = r.comment || "";
      content = String(content).replace(/^[\s"“”'‘’]+|[\s"“”'‘’]+$/g, '');

      return {
        id: r.reviewId,
        name: r.reviewer?.displayName || "Anonymous",
        content: content || "",
        date: date,
        rating: rating,
        hasReply: r.reviewReply && typeof r.reviewReply.comment === "string" && r.reviewReply.comment.trim().length > 0, // The replay should be non-empty string
        reply: r.reviewReply && typeof r.reviewReply.comment === "string" ? r.reviewReply.comment.trim() : "",
      }
    });

    console.log("\nFetched reviews:", fetchedReviews); //debug log
    console.log("\nNormalized reviews:", normalizedReviews); //debug log
    setReviews(normalizedReviews);
    setPairs(chunk(normalizedReviews, 2));

    //TODO: need to make the search term filter work here by sorting in the frontend, google doesn't take a search term
  }

  // Automatically fetch reviews when filters or page number changes.
  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        await fetchReviews(pageNumber, pageSize, biLayer, filters);
      } catch (err) {
        if (mounted) {
          console.error('Failed to fetch reviews', err);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [pageNumber, filters]);

  return (
    <>
      <PopupModal
        open={popUpOpen}
        title={popUpTitle}
        message={popUpMessage}
        onClose={() => setPopupOpen(false)}
      />
      <div
        className="reviewContainer"
        id="reviewContainer"
      >
        {pairs.map((pair) => (
          pair.length === 2 ? (
            <div
              className="reviewSubContainer"
              key={pair[0].id + ", " + pair[1].id}
            >
              <div
                className="review"
                key={pair[0].id}
                id={"review-" + pair[0].id}
                reviewId={pair[0].id}
                onClick={() => setReviewDetails(pair[0])}
              >
                <div className="reviewHeader">
                  <h3>{pair[0].name}</h3>
                  {pair[0].hasReply ? <span className="replyIndicator">✓</span> : null}
                </div>
                <div className="date">{pair[0].date}</div>
                <div className="rating">
                  {"★".repeat(pair[0].rating)}{"☆".repeat(5 - pair[0].rating)}
                </div>
                <p>{pair[0].content}</p>
              </div>

              <div
                className="review"
                key={pair[1].id}
                id={"review-" + pair[1].id}
                reviewId={pair[1].id}
                onClick={() => setReviewDetails(pair[1])}
              >
                <div className="reviewHeader">
                  <h3>{pair[1].name}</h3>
                  {pair[1].hasReply ? <span className="replyIndicator">✓</span> : null}
                </div>
                <div className="date">{pair[1].date}</div>
                <div className="rating">
                  {"★".repeat(pair[1].rating)}{"☆".repeat(5 - pair[1].rating)}
                </div>
                <p>{pair[1].content}</p>
              </div>
            </div>
          ) : (
            <div
              className="reviewSubContainer"
              key={pair[0].id}
            >
              <div
                className="review"
                key={pair[0].id}
                id={"review-" + pair[0].id}
                reviewId={pair[0].id}
                onClick={() => setReviewDetails(pair[0])}
              >
                <div className="reviewHeader">
                  <h3>{pair[0].name}</h3>
                  {pair[0].hasReply ? <span className="replyIndicator">✓</span> : null}
                </div>
                <div className="date">{pair[0].date}</div>
                <div className="rating">
                  {"★".repeat(pair[0].rating)}{"☆".repeat(5 - pair[0].rating)}
                </div>
                <p>{pair[0].content}</p>
              </div>
            </div>
          )
        ))}

        {/* Pagination controls */}
        <div className="pagination">
          <button
            onClick={() => setPageNumber(pageNumber - 1)}
            disabled={pageNumber === 1}
          >
            ← Prev
          </button>
          <span>Page {pageNumber}</span>
          <button
            onClick={() => setPageNumber(pageNumber + 1)}
            disabled={
              totalReviews === null ||
              pageNumber * pageSize >= totalReviews
            }
          >
            Next →
          </button>
        </div>

        {/* Render review details if available */}
        {reviewDetails && (
          <div
            className="modal-overlay"
            onMouseDown={(e) => {
              // Only close when the press starts on the backdrop itself so onMouseDown instead of onClick.
              // Using onClick closes when a press starts inside the modal and is
              // released over the backdrop, because the click targets this overlay.
              if (e.target === e.currentTarget) {
                setReviewDetails(null);
              }
            }}
          >
            <div
              className="modal-content"
              onClick={e => e.stopPropagation()}
            >
              <ReviewDetails review={reviewDetails} setReviewDetails={setReviewDetails} />
            </div>
          </div>
        )}

      </div>
    </>
  )
}